import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Database, Download, Loader2, ShieldAlert, Clock, FileJson } from 'lucide-react';
import AdminLayout from '../components/AdminLayout';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Feb 2026 — Backup Stock Data (READ-ONLY, super-admin only).
 *
 * One button that pulls a single JSON file containing the current
 * `stock_events` ledger, `products` (stock_qty + batches), and
 * `warehouse_placements`. The filename carries the UTC date/time of
 * the download so you can keep multiple snapshots side-by-side.
 *
 * The "last backup" timestamp is tracked PER BROWSER in
 * localStorage — the server stores nothing. This keeps the whole
 * surface strictly read-only.
 */

const LS_KEY = 'mr_backup_stock_last_at';

export default function AdminBackupStockData() {
  const token = localStorage.getItem('mr_admin_token') || '';
  const authHeaders = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const [stats, setStats] = useState(null);
  const [loadingStats, setLoadingStats] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [forbidden, setForbidden] = useState(false);
  const [lastBackupAt, setLastBackupAt] = useState(() => {
    try {
      return localStorage.getItem(LS_KEY) || '';
    } catch (_) {
      return '';
    }
  });

  const loadStats = useCallback(async () => {
    setLoadingStats(true);
    try {
      const { data } = await axios.get(
        `${API}/admin/backup-stock-data/stats`,
        { headers: authHeaders },
      );
      setStats(data);
      setForbidden(false);
    } catch (err) {
      if (err.response?.status === 403) {
        setForbidden(true);
        setStats(null);
      } else {
        toast.error(err.response?.data?.detail || 'Failed to load counts.');
      }
    } finally {
      setLoadingStats(false);
    }
  }, [authHeaders]);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  const triggerDownload = useCallback(async () => {
    setDownloading(true);
    try {
      const res = await axios.get(
        `${API}/admin/backup-stock-data`,
        { headers: authHeaders, responseType: 'blob' },
      );
      // Derive filename from the server's Content-Disposition if
      // present, else build one locally that matches the server
      // convention.
      const cd = res.headers['content-disposition'] || '';
      const match = /filename="([^"]+)"/.exec(cd);
      const now = new Date();
      const stamp = `${now.getUTCFullYear()}-${pad(now.getUTCMonth() + 1)}-${pad(now.getUTCDate())}-${pad(now.getUTCHours())}${pad(now.getUTCMinutes())}${pad(now.getUTCSeconds())}`;
      const filename = match?.[1] || `stock-backup-${stamp}.json`;
      const blob = new Blob([res.data], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      // Record the download time locally. We never persist it on
      // the server to keep the audit surface strictly read-only.
      const nowIso = new Date().toISOString();
      try {
        localStorage.setItem(LS_KEY, nowIso);
      } catch (_) { /* storage quota / private mode */ }
      setLastBackupAt(nowIso);
      toast.success(`Downloaded ${filename}`);
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Backup download failed.');
    } finally {
      setDownloading(false);
    }
  }, [authHeaders]);

  if (forbidden) {
    return (
      <AdminLayout>
        <div
          className="max-w-xl mx-auto mt-24 bg-rose-50 border border-rose-200 rounded-xl p-8 text-center"
          data-testid="backup-stock-data-forbidden"
        >
          <ShieldAlert className="w-10 h-10 text-rose-600 mx-auto mb-3" />
          <div className="text-lg font-semibold text-rose-900">Super admin only</div>
          <div className="text-sm text-rose-700 mt-2">
            Backup Stock Data is restricted to super administrators.
            This page is read-only and the backup is never restored
            automatically.
          </div>
        </div>
      </AdminLayout>
    );
  }

  const total = stats
    ? (stats.counts.stock_events + stats.counts.products + stats.counts.warehouse_placements)
    : 0;

  return (
    <AdminLayout>
      <div className="max-w-3xl mx-auto space-y-6" data-testid="backup-stock-data-page">
        {/* ---------- Header ---------- */}
        <div>
          <div className="flex items-center gap-2">
            <Database className="w-6 h-6 text-indigo-600" />
            <h1 className="text-2xl font-semibold text-neutral-900">
              Backup Stock Data
            </h1>
            <span className="px-2 py-0.5 text-[11px] font-semibold rounded-full bg-indigo-100 text-indigo-700">
              Read-only
            </span>
          </div>
          <div className="text-sm text-neutral-600 mt-1">
            One click to download a point-in-time copy of the full
            stock state as a single JSON file. The server stores
            nothing about this download.
          </div>
        </div>

        {/* ---------- Important note ---------- */}
        <div
          className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 leading-relaxed"
          data-testid="backup-stock-data-note"
        >
          <strong className="font-semibold">Note.</strong>{' '}
          This file is a <strong>copy</strong> of the current stock
          state only. It is not restored automatically and nothing in
          the running system reads it back. Keep the file somewhere
          safe (external drive, cloud) if you need it for recovery
          later. Restoring from a backup is a manual, super-admin-only
          operation handled off-line.
        </div>

        {/* ---------- Last backup ---------- */}
        <div
          className="rounded-lg border border-neutral-200 bg-white px-4 py-3 text-sm flex items-center gap-2"
          data-testid="backup-stock-data-last-at"
        >
          <Clock className="w-4 h-4 text-neutral-500" />
          <span className="text-neutral-700">Last backup on this browser:</span>
          <span className="font-semibold text-neutral-900">
            {lastBackupAt ? fmtTs(lastBackupAt) : 'Never'}
          </span>
          {lastBackupAt && (
            <span className="text-[11px] text-neutral-500 ml-auto">
              {relative(lastBackupAt)}
            </span>
          )}
        </div>

        {/* ---------- What's inside ---------- */}
        <div
          className="rounded-lg border border-neutral-200 bg-white px-4 py-3 text-sm"
          data-testid="backup-stock-data-contents"
        >
          <div className="flex items-center gap-2 text-neutral-700 font-semibold">
            <FileJson className="w-4 h-4 text-indigo-600" />
            What's inside the file
          </div>
          <ul className="mt-2 ml-6 list-disc text-neutral-700 space-y-1">
            <li>
              <span className="font-mono">stock_events</span> — the full
              stock ledger
              {stats && (
                <span className="ml-1 text-neutral-500">
                  ({fmtInt(stats.counts.stock_events)} rows)
                </span>
              )}
            </li>
            <li>
              <span className="font-mono">products</span> — SKU, current
              <span className="font-mono mx-1">stock_qty</span> and all batches
              {stats && (
                <span className="ml-1 text-neutral-500">
                  ({fmtInt(stats.counts.products)} products)
                </span>
              )}
            </li>
            <li>
              <span className="font-mono">warehouse_placements</span> —
              physical rack state
              {stats && (
                <span className="ml-1 text-neutral-500">
                  ({fmtInt(stats.counts.warehouse_placements)} placements)
                </span>
              )}
            </li>
          </ul>
          {stats && (
            <div className="mt-3 text-xs text-neutral-500" data-testid="backup-stock-data-total">
              Total rows: <span className="font-semibold">{fmtInt(total)}</span>
            </div>
          )}
        </div>

        {/* ---------- Download button ---------- */}
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={triggerDownload}
            disabled={downloading || loadingStats}
            className="px-5 py-2.5 rounded-lg bg-indigo-600 text-white font-semibold hover:bg-indigo-700 flex items-center gap-2 disabled:opacity-50 shadow-sm"
            data-testid="backup-stock-data-download"
          >
            {downloading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Preparing backup…
              </>
            ) : (
              <>
                <Download className="w-4 h-4" />
                Download Backup
              </>
            )}
          </button>
          <span className="text-xs text-neutral-500">
            The filename carries the UTC date and time of the download
            (e.g. <span className="font-mono">stock-backup-2026-10-05-083012.json</span>).
          </span>
        </div>

        <div className="text-[11px] text-neutral-400 text-right">
          This page is strictly read-only. The backend endpoint
          <code className="mx-1">/api/admin/backup-stock-data</code>
          never mutates stock, ledger, batch or placement data.
        </div>
      </div>
    </AdminLayout>
  );
}

// ---------------------------------- helpers ----------------------------------

function pad(n) {
  return String(n).padStart(2, '0');
}

function fmtInt(n) {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return '—';
  return Number(n).toLocaleString();
}

function fmtTs(ts) {
  if (!ts) return '';
  try {
    const d = new Date(ts);
    if (Number.isNaN(d.getTime())) return String(ts);
    return d.toLocaleString(undefined, {
      year: 'numeric', month: 'short', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
    });
  } catch (_) {
    return String(ts);
  }
}

function relative(ts) {
  try {
    const d = new Date(ts);
    const secs = (Date.now() - d.getTime()) / 1000;
    if (secs < 60) return 'moments ago';
    const mins = secs / 60;
    if (mins < 60) return `${Math.round(mins)} min ago`;
    const hrs = mins / 60;
    if (hrs < 24) return `${Math.round(hrs)}h ago`;
    const days = hrs / 24;
    return `${Math.round(days)}d ago`;
  } catch (_) {
    return '';
  }
}
